import React from 'react';
import { motion } from 'framer-motion';
import { Eye, ShieldAlert, LineChart, Target } from 'lucide-react';

const OSINTBenefits = () => {
  const benefits = [
    {
      title: "Visibilité des données",
      icon: <Eye size={18} />,
      description: "Identifier les informations sensibles exposées publiquement sur le web, les réseaux sociaux et le darknet."
    },
    {
      title: "Réduction des risques",
      icon: <ShieldAlert size={18} />,
      description: "Réduire drastiquement la surface d'attaque numérique de votre organisation en fermant les brèches d'information."
    },
    {
      title: "Anticipation des menaces",
      icon: <LineChart size={18} />,
      description: "Détecter les signes avant-coureurs d'une cyberattaque ou d'une campagne de désinformation avant qu'elles ne frappent."
    },
    {
      title: "Renforcement stratégique",
      icon: <Target size={18} />,
      description: "Améliorer la stratégie globale de cybersécurité en intégrant l'intelligence tactique dans vos processus décisionnels."
    }
  ];

  return (
    <section className="w-full bg-white py-16 px-6 flex flex-col items-center">
      
      {/* Top Header Section from Figma */}
      <div className="text-center mb-16 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#eef5ff] mb-6"
        >
          <span className="text-[#3b82f6] text-[10px] font-extrabold uppercase tracking-widest">
            Cyber-Intelligence
          </span>
        </motion.div>

        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-[36px] md:text-[48px] font-black text-[#0f172a] mb-6 tracking-tight leading-[1.1]"
        >
          Maîtrisez votre empreinte numérique
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-[#64748b] text-[15px] font-medium max-w-3xl mx-auto leading-relaxed"
        >
          L'OSINT (Open Source Intelligence) transforme les données publiques en renseignements exploitables pour protéger votre entreprise.
        </motion.p>
      </div>

      {/* Grid Header - Les bénéfices de l'OSINT */}
      <div className="w-full max-w-[1100px] mb-8 text-left border-l-4 border-[#3b82f6] pl-4">
        <motion.h3 
          initial={{ opacity: 0, x: -10 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="text-[24px] font-black text-[#0f172a] tracking-tight"
        >
          Les bénéfices de l'OSINT
        </motion.h3>
      </div>

      {/* Grid - Cards exactly like Figma */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-[1100px] w-full">
        {benefits.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            whileHover={{ y: -5 }}
            className="group p-7 rounded-2xl border border-slate-100 flex flex-col items-start transition-all duration-300"
            style={{ backgroundColor: '#f8fafc' }}
          >
            <div className="p-2.5 rounded-xl mb-6 text-[#3b82f6] bg-white shadow-sm transition-all duration-500 group-hover:bg-[#3b82f6] group-hover:text-white">
              {item.icon}
            </div>

            <h4 className="text-[#0f172a] text-base font-extrabold mb-4 leading-snug">
              {item.title}
            </h4>

            <p className="text-[#64748b] text-[12px] font-medium leading-relaxed">
              {item.description}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default OSINTBenefits;